import { SxProps, Theme } from '@mui/material/styles';
import { isEmpty } from 'utils/util';
import { TConsultaEstoque } from './ConsultaEstoque.service';

// DotNestedKeys é redeclarado localmente (em vez de importado de utils/util) porque importar o
// tipo genérico de lá e combiná-lo com SxProps/interseções trava o compilador do TypeScript 4.9.5
// (RangeError: Maximum call stack size exceeded em outofbandVarianceMarkerHandler). Ver
// logs/BUGFIX-consulta-estoque-typescript-stack-overflow.md.
type DotNestedKeys<T> = {
    [K in keyof T & string]: T[K] extends (infer U)[]
    ? U extends object
    ? `${K}.${number}.${DotNestedKeys<U>}`
    : `${K}.${number}`
    : T[K] extends object
    ? `${K}.${DotNestedKeys<T[K]>}`
    : K;
}[keyof T & string];

type ParametroFiltro = { filterValue: string; item: unknown; row: TConsultaEstoque; };

const texto = (valor: unknown) => String(valor ?? '').toLowerCase().trim();

const paraNumero = (valor: string): number => {
    const limpo = valor.replace(/[^\d,.-]/g, '');
    const semSeparadorDeMilhar = limpo.includes(',')
        ? limpo.replace(/\./g, '').replace(',', '.')
        : limpo.replace(/\.(?=\d{3}(?:\.|$))/g, '');
    return isEmpty(semSeparadorDeMilhar) ? Number.NaN : Number(semSeparadorDeMilhar);
};

export type ColunaConsultaEstoque = {
    id: string;
    key: DotNestedKeys<TConsultaEstoque>;
    header: string;
    textAlign?: "center" | "left" | "right";
    filterable?: boolean;
    customFilter?: (param: ParametroFiltro) => boolean;
    width: number | string;
    sx: SxProps<Theme>;
};

export const LARGURA_IMAGEM = { width: '6%', whiteSpace: 'nowrap', px: 2 } as const;

export const LARGURA_ESTABELECIMENTO = { width: '14%', whiteSpace: 'nowrap', px: 2 } as const;

export const LARGURA_PRODUTO = { width: '65%', whiteSpace: 'nowrap', px: 2 } as const;

export const LARGURA_ESPACADA = { width: '1%', whiteSpace: 'nowrap', px: 1.5 } as const;

type Largura =
    | typeof LARGURA_IMAGEM
    | typeof LARGURA_ESTABELECIMENTO
    | typeof LARGURA_PRODUTO
    | typeof LARGURA_ESPACADA;

// A largura da coluna é informada uma única vez: o `width` que o ColumnData exige e o `sx` que
// o MUI consome saem os dois da mesma constante, em vez de repetí-la em cada coluna.
const comLargura = (
    coluna: Omit<ColunaConsultaEstoque, 'width' | 'sx'>,
    largura: Largura
): ColunaConsultaEstoque => ({ ...coluna, width: largura.width, sx: largura });

const filtroContem = ({ filterValue, item }: ParametroFiltro) => texto(item).includes(texto(filterValue));

const filtroExato = ({ filterValue, item }: ParametroFiltro) => {
    const digitado = texto(filterValue);
    if (isEmpty(digitado)) return true;
    if (item === null || item === undefined) return false;

    const numeroDigitado = paraNumero(digitado);
    const numeroItem = Number(item);

    return !Number.isNaN(numeroDigitado) && !Number.isNaN(numeroItem)
        ? numeroDigitado === numeroItem
        : texto(item) === digitado;
};

const COLUNA_ESTABELECIMENTO = comLargura({
    id: 'estabelecimento',
    key: 'estabelecimento',
    header: 'Estabelecimento',
    textAlign: 'left',
    filterable: true,
    customFilter: filtroContem,
}, LARGURA_ESTABELECIMENTO);

const COLUNA_PRODUTO = comLargura({
    id: 'produto',
    key: 'sku.descritivo',
    header: 'Produto, Nome, Cor e Tamanho',
    textAlign: 'left',
    filterable: true,
    customFilter: filtroContem,
}, LARGURA_PRODUTO);

const COLUNA_QUANTIDADE = comLargura({
    id: 'quantidade',
    key: 'sku.estoque.saldoEstoque',
    header: 'Quantidade',
    textAlign: 'left',
    filterable: true,
    customFilter: filtroExato,
}, LARGURA_ESPACADA);

const COLUNA_DEPOSITO = comLargura({
    id: 'deposito',
    key: 'sku.estoque.nomeDeposito',
    header: 'Depósito',
    textAlign: 'left',
    filterable: true,
    customFilter: filtroContem,
}, LARGURA_ESPACADA);

const COLUNA_VALOR = comLargura({
    id: 'valor',
    key: 'sku.preco',
    header: 'Valor',
    textAlign: 'left',
    filterable: true,
    customFilter: filtroExato,
}, LARGURA_ESPACADA);

export const COLUNAS_FILTRAVEIS: ColunaConsultaEstoque[] = [
    COLUNA_ESTABELECIMENTO,
    COLUNA_PRODUTO,
    COLUNA_QUANTIDADE,
    COLUNA_DEPOSITO,
    COLUNA_VALOR,
];
